import moment, { Moment } from 'moment-timezone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { getDatesRange, getTimezone } from '@mgm/common/utils';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { QUERY_KEYS } from '../../../../utils/enums';
import { IBaseResponse } from '../../../../types/responses';
import { OrdersController } from '../../../../services/orders.api';
import { TGetOrdersMappingRes, TGetOrdersRes } from '../../../../types/responses/orders.type';
import { ordersActions } from '../../../../store/orders/slice';
import { TAssignBulk, TAssignMappingReq, TReassignMappingReq } from '../../../../types/requests';
import { useError } from '../../../../hooks/useCommon';
import { getUsersSummary } from '../../../../utils/helpers/commission.helper';
import { getMembers } from '../../../../store/team/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import {
  getDorChosenDate,
  getOrdersChosenDates,
  getOrdersMapping,
} from '../../../../store/orders/selectors';
import { useMergeCommission } from './useOrders';
import { getCommissionPlans, getCommissionTeams } from '../../../../store/commissions/selectors';
import { getSheets } from '../../../../store/sheets/selectors';
import { TCommissionPlan, TPromoterTotal } from '../../../../types';
import { useStats } from '../../DailyStats/hooks/useStats';

type TGetOrders = {
  dates?: [Moment, Moment],
  isMainOrdersPage?: boolean
};

export function useGetOrdersMappingAPI(options?: TGetOrders) {
  const dispatch = useAppDispatch();
  const { personnel, shop } = useAppSelector(getAuthenticateUser);

  const { handleReqError } = useError();

  const { toDate, fromDate } = getDatesRange(options?.dates, false, false, shop.shopTimezone);

  const startMoment = moment(fromDate);
  const endMoment = moment(toDate);

  const monthYears: string[] = [];
  const currentMoment = startMoment.clone();

  while (currentMoment.isSameOrBefore(endMoment, 'month')) {
    monthYears.push(currentMoment.format('YYYY-MM'));
    currentMoment.add(1, 'month');
  }

  return useQuery(
    [QUERY_KEYS.ORDERS.GET_MAPPING],
    async () => {
      const data = {
        shopName: personnel.shopName,
        monthYears,
      };

      const rsp = await OrdersController().getOrdersMapping<TGetOrdersMappingRes>(data);

      if (rsp?.success && 'ordersMapping' in rsp) {
        dispatch(ordersActions.setOrdersMapping(rsp.ordersMapping));

        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get shopify orders failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      suspense: false,
      enabled: true,
    },
  );
}

export function useGetOrderFirstPageAPI(options?: TGetOrders) {
  const dispatch = useAppDispatch();
  const members = useAppSelector(getMembers);
  const ordersMapping = useAppSelector(getOrdersMapping);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const sheets = useAppSelector(getSheets);

  const { personnel, shop } = useAppSelector(getAuthenticateUser);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const dorChosenDate = useAppSelector(getDorChosenDate);

  const { handleReqError } = useError();

  const { mergeCommissionPlansIntoOrders } = useMergeCommission();
  const {
    calculateSalesAndCommissions,
    calculateSalesByChannel,
    getOrderTotalsByLocation,
    aggregateDataByLocation,
    ordersByLocation,
    calculateOrdersByMember,
    getStopperSummary,
  } = useStats();

  const byWeek = !options?.dates;

  const { fromDate, toDate } = getDatesRange(
    options?.dates,
    byWeek,
    false,
    shop?.shopTimezone,
  );

  return useQuery(
    [QUERY_KEYS.ORDERS.GET_ORDERS],
    async () => {
      const data = {
        shopName: personnel.shopName,
        from: fromDate,
        to: toDate,
        timezone: shop?.shopTimezone,
      };

      dispatch(ordersActions.setIsOrdersLoading(true));

      const rsp = await OrdersController().getOrderFirstPage<TGetOrdersRes>(data);

      if (rsp?.success && 'orders' in rsp) {
        dispatch(commissionsActions.resetCommissionsState());
        dispatch(ordersActions.resetOrdersState());

        const orders = mergeCommissionPlansIntoOrders(rsp.orders, ordersMapping);

        let filteredOrders = orders;

        if (personnel.permissionLevel !== 'Admin') {
          const allowedLocationIds = personnel.locations.map((location) => location.id);
          filteredOrders = orders.filter((order) => allowedLocationIds.includes(order.locationId));
        }

        const data = getUsersSummary(filteredOrders, members, commissionPlans[0], sheets, commissionTeams, shop.shopTimezone);

        const promotersTotals = getStopperSummary(filteredOrders);

        dispatch(ordersActions.setPromotersTotals(promotersTotals as unknown as Array<TPromoterTotal>));

        const ordersByMember = calculateOrdersByMember(filteredOrders);

        const employeeRates = calculateSalesAndCommissions(ordersByMember);

        dispatch(ordersActions.processBestSalesman(ordersByMember));
        dispatch(ordersActions.processDataByMemberAndLocation(ordersByMember));

        const channelData = calculateSalesByChannel(filteredOrders);
        const locationStats = aggregateDataByLocation(filteredOrders);

        dispatch(ordersActions.setEmployeeRates(employeeRates));
        dispatch(ordersActions.setChannelData(channelData));
        dispatch(ordersActions.setLocationStats(locationStats));

        const ordersTotals = getOrderTotalsByLocation(filteredOrders);
        const orderLocations = ordersByLocation(filteredOrders);

        const ordersByDate = filteredOrders.filter((order) => {
          const orderDate = moment.tz(order.processedAt, shop?.shopTimezone).startOf('day').format();
          const dorDate = moment.tz(dorChosenDate, shop?.shopTimezone).startOf('day').format();

          return orderDate === dorDate;
        });

        dispatch(commissionsActions.setCommissionsSummary(data));
        dispatch(ordersActions.setAttributedOrders(filteredOrders));
        dispatch(ordersActions.mergeOrderTotalsByLocation(ordersTotals));
        dispatch(ordersActions.setOrdersByDates(ordersByDate));
        dispatch(ordersActions.mergeOrdersByLocation(orderLocations));

        if (!rsp.hasNext) {
          dispatch(ordersActions.setIsOrdersLoading(false));
        }

        return rsp as TGetOrders;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
        dispatch(ordersActions.setIsOrdersLoading(false));
      } else if ('msg' in rsp) {
        console.error(`ERROR! get orders failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      suspense: false,
      enabled: options?.isMainOrdersPage || false,
    },
  );
}

export function useAssignBulkAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const queryClient = useQueryClient();

  const assignBulkApi = (payload: Pick<TAssignBulk, 'ordersData'>) => {
    const data = {
      ordersData: payload.ordersData,
      shopName: personnel.shopName,
      timezone: getTimezone(),
    };

    return OrdersController().assignBulk<IBaseResponse>(data);
  };

  return useMutation(assignBulkApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS.GET_ORDERS] });

        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! assign bulk failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! assign bulk request threw an Exception! ${err}`);
    },
  });
}

interface TAssignOrder {
  orderId: string;
  commissionPlan?: TCommissionPlan;
  team?: string;
}

export function useAssignOrdersAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const { refetch: refetchMappings } = useGetOrdersMappingAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const assignOrdersApi = (payload: {
    orders: { [key: string]: Array<TAssignOrder> },
    monthYear: string
  }) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return OrdersController().assignOrders<IBaseResponse>(data);
  };

  return useMutation(assignOrdersApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await refetchMappings();
        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! reassign order failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! reassign order request threw an Exception! ${err}`);
    },
  });
}

export function useReassignOrderAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const { refetch: refetchMappings } = useGetOrdersMappingAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const reassignOrderApi = (payload: {
    orderId: string;
    email: string;
    team?: string;
    userId?: string;
  }) => {
    const data = {
      shopName: personnel.shopName,
      orderData: payload,
    };

    return OrdersController().reassignOrder<IBaseResponse>(data);
  };

  return useMutation(reassignOrderApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await refetchMappings();
        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! reassign order failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! reassign order request threw an Exception! ${err}`);
    },
  });
}

export function useReassignMappingAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const { enqueueSnackbar } = useSnackbar();

  const { refetch: refetchMappings } = useGetOrdersMappingAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const reassignMappingApi = (payload: Omit<TReassignMappingReq, 'shopName'>) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return OrdersController().reassignMapping<IBaseResponse>(data);
  };

  return useMutation(reassignMappingApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${`Order reassigned from ${variables?.previousEmail} to ${variables?.mapping?.email} successfully.`}`, {
          variant: 'success',
          autoHideDuration: 4000,
        });
        await refetchMappings();
        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! reassign mapping failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! reassign mapping request threw an Exception! ${err}`);
    },
  });
}

export function useAssignMappingsAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const queryClient = useQueryClient();

  const assignMappingApi = (payload: Omit<TAssignMappingReq, 'shopName'>) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return OrdersController().assignMappings<IBaseResponse>(data);
  };

  return useMutation(assignMappingApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS.GET_MAPPING] });
        return rsp;
      } if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! assign mapping failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err) => {
      console.error(`ERROR! assign mapping request threw an Exception! ${err}`);
    },
  });
}
